/**
 * California statewide GHG by sector for stacked charts.
 * Built by summing published community GHG release county rows (populate / fetchGHGInventoryData)
 * and geofips in the California county band 6000–6999 (same basis as the county stacked
 * total chart for CA), not from geography_type = 'State'.
 */

import {
  canonicalInventoryGeofipsKey,
  inventoryGeofipsNumeric,
  preferNewerPrecalcRow,
} from './ghgInventoryGeofipsCanonical';
import {
  dedupeReleaseRowsByCanonYear,
  fetchCommunityGhgReleaseMetadata,
  fetchCommunityGhgReleaseRows,
} from './communityGhgInventoryReleaseService';

export interface CaliforniaStateSectorFetchOptions {
  yearMin?: number;
  yearMax?: number;
}

export interface StateSectorYearRow {
  year: number;
  ldv_emissions: number | null;
  hdv_emissions: number | null;
  elec_total_emissions: number | null;
  ng_total_emissions: number | null;
  waste_emissions: number | null;
  offroad_emissions: number | null;
  agriculture_emissions: number | null;
  total_emissions: number | null;
}

export interface CaliforniaStateSectorPayload {
  years: number[];
  rows: StateSectorYearRow[];
  locationName: string | null;
  error: string | null;
  /** True when series is summed from county rows (always for this service). */
  aggregatedFromCounties: boolean;
  release?: { releaseId: string; releasedAt: string | null; calculationVersion: string | null };
}

function num(v: unknown): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

type CountySectorRow = {
  geofips: number;
  year: number;
  location_name: string;
  ldv_emissions: number | null;
  hdv_emissions: number | null;
  elec_total_emissions: number | null;
  ng_total_emissions: number | null;
  waste_emissions: number | null;
  offroad_emissions: number | null;
  agriculture_emissions: number | null;
  total_emissions: number | null;
};

type CountySectorRaw = CountySectorRow & {
  calculation_date: string | null;
  geofipsRaw: unknown;
};

function addToSum(acc: number, v: number | null): number {
  if (v != null && Number.isFinite(v)) return acc + v;
  return acc;
}

/**
 * Fetch all California county GHG rows, dedupe canonical geofips per year (newest calculation_date) —
 * avoids double-counting leading-zero key variants and duplicate upserts.
 * Then sum by year across counties.
 */
export async function fetchCaliforniaStateGhgBySector(
  options?: CaliforniaStateSectorFetchOptions
): Promise<CaliforniaStateSectorPayload> {
  const releaseMeta = await fetchCommunityGhgReleaseMetadata();
  const { rows: releaseRows, error } = await fetchCommunityGhgReleaseRows({
    geographyType: 'County',
    yearMin: options?.yearMin,
    yearMax: options?.yearMax,
    select:
      'geofips,year,location_name,calculation_date,ldv_emissions,hdv_emissions,elec_total_emissions,ng_total_emissions,waste_emissions,offroad_emissions,agriculture_emissions,total_emissions',
  });

  if (error) {
    return {
      years: [],
      rows: [],
      locationName: null,
      error,
      aggregatedFromCounties: true,
    };
  }

  const raw: CountySectorRaw[] = [];
  for (const r of dedupeReleaseRowsByCanonYear(releaseRows).values()) {
    raw.push({
      geofips: r.geofips,
      geofipsRaw: r.geofipsRaw,
      year: r.year,
      location_name: r.location_name,
      calculation_date: r.calculation_date,
      ldv_emissions: r.ldv_emissions,
      hdv_emissions: r.hdv_emissions,
      elec_total_emissions: r.elec_total_emissions,
      ng_total_emissions: r.ng_total_emissions,
      waste_emissions: r.waste_emissions,
      offroad_emissions: r.offroad_emissions,
      agriculture_emissions: r.agriculture_emissions,
      total_emissions: r.total_emissions,
    });
  }

  if (raw.length === 0) {
    return {
      years: [],
      rows: [],
      locationName: 'California',
      error: null,
      aggregatedFromCounties: true,
    };
  }

  const byCountyYear = new Map<string, CountySectorRaw>();
  for (const r of raw) {
    const name = r.location_name;
    if (!name) continue;
    if (name.toLowerCase() === 'california') continue;
    if (!Number.isFinite(r.year)) continue;
    const canon = canonicalInventoryGeofipsKey(r.geofipsRaw);
    if (!canon) continue;
    const key = `${canon}|${r.year}`;
    const prev = byCountyYear.get(key);
    byCountyYear.set(key, prev ? preferNewerPrecalcRow(prev, r) : r);
  }

  const yearTotals = new Map<
    number,
    {
      ldv: number;
      hdv: number;
      elec: number;
      ng: number;
      waste: number;
      offroad: number;
      ag: number;
      total: number;
    }
  >();

  for (const r of Array.from(byCountyYear.values())) {
    const y = r.year;
    if (!yearTotals.has(y)) {
      yearTotals.set(y, { ldv: 0, hdv: 0, elec: 0, ng: 0, waste: 0, offroad: 0, ag: 0, total: 0 });
    }
    const t = yearTotals.get(y)!;
    t.ldv = addToSum(t.ldv, r.ldv_emissions);
    t.hdv = addToSum(t.hdv, r.hdv_emissions);
    t.elec = addToSum(t.elec, r.elec_total_emissions);
    t.ng = addToSum(t.ng, r.ng_total_emissions);
    t.waste = addToSum(t.waste, r.waste_emissions);
    t.offroad = addToSum(t.offroad, r.offroad_emissions);
    t.ag = addToSum(t.ag, r.agriculture_emissions);
    t.total = addToSum(t.total, r.total_emissions);
  }

  let years = Array.from(yearTotals.keys()).sort((a, b) => a - b);
  if (options?.yearMin != null) {
    years = years.filter((y) => y >= options.yearMin!);
  }
  if (options?.yearMax != null) {
    years = years.filter((y) => y <= options.yearMax!);
  }
  const rows: StateSectorYearRow[] = years.map((year) => {
    const t = yearTotals.get(year)!;
    return {
      year,
      ldv_emissions: t.ldv,
      hdv_emissions: t.hdv,
      elec_total_emissions: t.elec,
      ng_total_emissions: t.ng,
      waste_emissions: t.waste,
      offroad_emissions: t.offroad,
      agriculture_emissions: t.ag,
      total_emissions: t.total,
    };
  });

  return {
    years,
    rows,
    locationName: 'California',
    error: null,
    aggregatedFromCounties: true,
    release: {
      releaseId: releaseMeta.releaseId,
      releasedAt: releaseMeta.releasedAt,
      calculationVersion: releaseMeta.calculationVersion,
    },
  };
}
